import Animated, { runOnJS } from 'react-native-reanimated';
import React, { useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { CursorValue, StyleProp, View, ViewStyle } from 'react-native';
import {
  Gesture,
  GestureDetector,
  GestureStateChangeEvent,
  GestureUpdateEvent,
  PanGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { useDelayedHover } from '@/hooks/use-delayed-hover';

interface Props {
  onGestureEnd: (
    event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
  ) => void;
  onGestureStart: (
    event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
  ) => void;
  onGestureUpdate: (
    event: GestureUpdateEvent<PanGestureHandlerEventPayload>,
  ) => void;
  style?: StyleProp<ViewStyle>;
  useWorklets?: boolean;
}

export const DragHandle: React.FC<Props> = ({
  onGestureEnd,
  onGestureStart,
  onGestureUpdate,
  style,
  useWorklets = false,
}) => {
  const [dragHandleActive, setDragHandleActive] = useState(false);
  const dragHandleRef = useRef<View>(null);
  const dragHandleHovered = useDelayedHover(dragHandleRef);
  const [styles] = useStyles();

  const dragHandlerPanGesture = useWorklets
    ? Gesture.Pan()
        .onStart((event) => {
          'worklet';
          onGestureStart(event);
          runOnJS(setDragHandleActive)(true);
        })
        .onUpdate(onGestureUpdate)
        .onEnd((event) => {
          'worklet';
          onGestureEnd(event);
          runOnJS(setDragHandleActive)(false);
        })
    : Gesture.Pan()
        .onStart((event) => {
          onGestureStart(event);
          setDragHandleActive(true);
        })
        .onUpdate(onGestureUpdate)
        .onEnd((event) => {
          onGestureEnd(event);
          setDragHandleActive(false);
        })
        .runOnJS(true);

  const ViewComponent = useWorklets ? Animated.View : View;

  return (
    <GestureDetector gesture={dragHandlerPanGesture}>
      <ViewComponent
        ref={dragHandleRef}
        style={[styles.dragHandleHitArea, style]}
      >
        <View
          style={[
            styles.dragHandle,
            dragHandleActive || dragHandleHovered
              ? styles.dragHandleActive
              : null,
          ]}
        />
      </ViewComponent>
    </GestureDetector>
  );
};

const useStyles = createStyles((theme) => ({
  dragHandle: {
    backgroundColor: theme.sk.color.divider.default,
    borderRadius: 2,
    height: '100%',
    width: 2,
  },
  dragHandleActive: {
    backgroundColor: theme.sk.color.icon.blue,
    width: 5,
  },
  dragHandleHitArea: {
    alignItems: 'center',
    cursor: 'ew-resize' as CursorValue,
    height: '100%',
    justifyContent: 'center',
    width: 12,
    zIndex: 10,
  },
}));
